<template>
  <div id="notification-container"
       class="header-notification notification-container-header"
       :class="'hn-' + notification_type"
       v-if="isShow">
    <div class="header-notification__content">
      <app-shell-icon class="header-notification__icon"
                      :name="icon"
                      :size="16"/>
      <div class="header-notification__body"
           ref="body"
           :id="bodyId"
           :class="{ 'is-clamped': !expanded }">
        {{ firstDiagnosis }}
        <template v-if="secondDiagnosis">
          <br>
          {{ secondDiagnosis }}
        </template>
        <a class="header-notification__link"
           target="_blank"
           rel="noopener noreferrer"
           data-testid="header-notification-learn-more-link"
           :href="link.learn_more_href"
           v-if="link && link.learn_more_href"
           @click="openLearnMoreLink">{{ link.learn_more_title ?? 'Learn more' }}</a>
      </div>
      <button class="header-notification__more"
              type="button"
              data-testid="header-notification-more-btn"
              :aria-controls="bodyId"
              :aria-expanded="String(expanded)"
              v-if="overflows || expanded"
              @click="expanded = !expanded">
        {{ expanded ? 'Less' : 'More' }}
      </button>
    </div>
    <div class="header-notification__actions"
         v-if="link && staticsLoaded && !statics.whitelabel">
      <button class="header-notification__action"
              type="button"
              data-testid="header-notification-action-btn"
              @click="openDiagnosisLink">
        {{ link.title }}
      </button>
    </div>
    <button class="header-notification__close"
            type="button"
            aria-label="Dismiss"
            data-testid="header-notification-close-btn"
            v-if="staticsLoaded && !statics.whitelabel"
            @click="close">
      <app-shell-icon name="x"
                      :size="16"/>
    </button>
  </div>
</template>

<script>
import { hasBridge, sendToMain } from 'src/plugins/helpers/electron'
import { mapGetters, mapState } from 'vuex'
import { aclMixin, classicMixin, kycMixin } from 'src/plugins/mixins'
import * as CompanyIssues from 'src/constants/company-issues'
import AppShellIcon from 'components/layout/app-shell-icon.vue'
import { bannerIcon } from 'src/plugins/helpers/app-header'

export default {
  name: 'header-notification',

  components: { AppShellIcon },

  mixins: [
    aclMixin,
    classicMixin,
    kycMixin
  ],

  computed: {
    ...mapState([
      'statics',
      'staticsLoaded'
    ]),

    ...mapState('cache', [
      'currentCompany'
    ]),

    ...mapGetters('auth', [
      'profile'
    ]),

    isShow () {
      return this.showNotification && this.diagnosis.length > 0
    },

    firstDiagnosis () {
      if (this.diagnosis.length > 0) {
        return this.diagnosis[0]
      }

      return 'We are having issues with your account and calls might not route at this time. Please contact support as soon as possible.'
    },

    secondDiagnosis () {
      if (this.diagnosis.length > 1 && this?.is10DlcError) {
        return this.diagnosis[1]
      }

      return ''
    },

    currentRouteName () {
      return this.$route.name
    },

    icon () {
      return bannerIcon(this.notification_type)
    },

    bodyId () {
      return `header-notification-body-${this._uid}`
    },

    is10DlcError () {
      return this.issueCodes.includes(CompanyIssues.ISSUE_A2P_10DLC_NOT_REGISTERED) ||
        this.issueCodes.includes(CompanyIssues.ISSUE_A2P_10DLC_INCOMPLETE) ||
        this.issueCodes.includes(CompanyIssues.ISSUE_BRAND_REGISTRATION_FAILED) ||
        this.issueCodes.includes(CompanyIssues.ISSUE_BRAND_NOT_REGISTERED) ||
        this.issueCodes.includes(CompanyIssues.ISSUE_BRAND_REGISTRATION_FAILED_TCR_ZERO)
    }
  },

  data: () => {
    return {
      issueCodes: [],
      diagnosis: [],
      link: null,
      notification_type: 'danger',
      showDiagnosis: false,
      showNotification: true,
      expanded: false,
      overflows: false,
      CompanyIssues
    }
  },

  mounted () {
    this.runDiagnosis()
  },

  beforeDestroy () {
    this.stopTrackingHeight()
  },

  methods: {
    // Views sized from 100vh subtract --header-notification-height to stay clear of the banner
    trackHeight () {
      this.heightObserver = new ResizeObserver(([entry]) => {
        document.documentElement.style.setProperty('--header-notification-height', `${entry.target.offsetHeight}px`)
        this.checkOverflow()
      })
      this.heightObserver.observe(this.$el)
    },

    stopTrackingHeight () {
      if (this.heightObserver) {
        this.heightObserver.disconnect()
        this.heightObserver = null
      }

      document.documentElement.style.removeProperty('--header-notification-height')
    },

    // Two lines show until the reader asks for the rest, so the banner never takes over the screen
    checkOverflow () {
      const body = this.$refs.body
      if (body && !this.expanded) {
        this.overflows = body.scrollHeight > body.clientHeight + 1
      }
    },

    runDiagnosis () {
      this.$axios.get(`/api/v1/status/diagnosis`).then(res => {
        this.issueCodes = res.data.issue_codes
        this.diagnosis = res.data.issues
        this.link = res.data.link
        this.notification_type = res.data.notification_type ?? 'danger'
        this.showDiagnosis = res.data.has_issues
      })
    },

    close () {
      this.showNotification = false
      this.$VueEvent.fire('headerNotificationClosed')
    },

    isAbsoluteURL (url) {
      let regEx = new RegExp('^(?:[a-z+]+:)?//', 'i')

      return regEx.test(url)
    },

    openDiagnosisLink (event) {
      if (event) {
        event.preventDefault()
      }
      this.openExternalUrl(this.getLink(this.link))
    },

    openLearnMoreLink (event) {
      event.preventDefault()
      this.openExternalUrl(this.link.learn_more_href)
    },

    openExternalUrl (url) {
      if (hasBridge()) {
        sendToMain('open_external_url', url)
        return
      }

      window.open(url, '_blank', 'noopener,noreferrer')
    },

    getLink (link) {
      if (!link.external || !this.isAbsoluteURL(link.href)) {
        return `${this.getClassicURL()}${link.href}`
      }

      return link.href
    }
  },
  watch: {
    isShow (value) {
      if (value) {
        document.body.classList.add('has-notification-banner')
        this.$nextTick(this.trackHeight)
      } else {
        document.body.classList.remove('has-notification-banner')
        this.stopTrackingHeight()
      }
    }
  }
}
</script>

<style lang="scss">
@import 'src/css/shell-tokens';

// A translucent tint laid over the page surface. Set it with `background`, not `background-color`.
@function banner-surface($tint) {
  @return linear-gradient($tint, $tint), $shell-color-surface;
}

$banner-icon-size: 16px;
$banner-line-height: 18px;

.header-notification {
  position: relative;
  z-index: 10;
  display: flex;
  gap: 16px;
  align-items: center;
  width: 100%;
  min-height: 50px;
  padding: 8px $shell-page-gutter;
  border-bottom: 1px solid $shell-color-border;
  background: banner-surface($shell-color-hover);
  color: $shell-color-text;
  font-size: $shell-text-sm;
  font-weight: $shell-weight-regular;
  line-height: $banner-line-height;

  &.hn-warning,
  &.hn-10dlc-warning {
    background: banner-surface($shell-color-warning-soft);
    color: $shell-color-warning;

    .header-notification__icon {
      color: $shell-color-warning-icon;
    }
  }

  &.hn-danger {
    background: banner-surface($shell-color-danger-soft);
    color: $shell-color-danger;
  }

  &.hn-success {
    background: banner-surface($shell-color-success-soft);
    color: $shell-color-success-text;
  }
}

.header-notification__content {
  display: flex;
  flex: 1;
  gap: 10px;
  align-items: flex-start;
  min-width: 0;
}

.header-notification__icon {
  flex: none;
  margin-top: ($banner-line-height - $banner-icon-size) / 2;
}

.header-notification__body {
  flex: 1;
  min-width: 0;

  &.is-clamped {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
}

// More / Less: an inline action in the banner's own ink, at the end of the clamped text
.header-notification__more {
  flex: none;
  align-self: flex-end;
  padding: 0;
  border: 0;
  border-radius: $shell-radius-sm;
  background: none;
  color: inherit;
  font: inherit;
  font-weight: $shell-weight-strong;
  text-decoration: underline;
  text-underline-offset: 2px;
  white-space: nowrap;
  cursor: pointer;
}

.header-notification__link {
  color: inherit;
  font-weight: $shell-weight-strong;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:hover {
    color: inherit;
    text-decoration-thickness: 2px;
  }
}

.header-notification__actions {
  display: flex;
  flex: none;
  gap: 8px;
  align-items: center;
}

.header-notification__action {
  display: inline-flex;
  align-items: center;
  height: $shell-control-height;
  padding: 0 12px;
  border: 1px solid $shell-color-border-strong;
  border-radius: $shell-radius-md;
  background-color: $shell-color-control;
  color: $shell-color-text;
  font-size: $shell-text-sm;
  font-weight: $shell-weight-strong;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color $shell-duration-hover-out ease,
    transform $shell-duration-press $shell-ease-out;

  &:active {
    transform: scale($shell-press-scale);
  }
}

.header-notification__close {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: $shell-control-height;
  height: $shell-control-height;
  margin-right: -8px;
  padding: 0;
  border: 0;
  border-radius: $shell-radius-md;
  background-color: transparent;
  color: inherit;
  cursor: pointer;
  transition: background-color $shell-duration-hover-out ease;
}

.header-notification__more,
.header-notification__action,
.header-notification__close {
  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px $shell-color-focus;
  }
}

@media (hover: hover) and (pointer: fine) {
  .header-notification__action:hover {
    background-color: $shell-color-control-hover;
    transition: transform $shell-duration-press $shell-ease-out;
  }

  .header-notification__close:hover {
    background-color: $shell-color-hover;
    transition: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .header-notification__action:active {
    transform: none;
  }
}
</style>
